import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Loader } from '@components';
import CurrencyFormat from '@components/CurrencyFormat';
import InfiniteList from '@components/InfiniteList';
import { useDebounce } from '@hooks/index';

import OrderDetails from './components/OrderDetails';
import { STATUS_OPTIONS, StatusFilter } from './constants';
import {
  CompactStatus,
  Content,
  DetailsLoader,
  DetailsPanel,
  EmptyDetails,
  EmptyList,
  Filters,
  HotelName,
  ListBody,
  ListError,
  ListLoader,
  Muted,
  NextPageLoader,
  OrderList,
  OrderNumber,
  OrderRow,
  Page,
  RowBottom,
  RowTop,
  SearchInput,
  StatusSelect,
  Title,
  Topbar,
} from './OrdersPage.styles';
import {
  fetchB2BOrdersAction,
  selectB2BOrderAction,
} from '../../../store/actions/b2bOrders';
import {
  clearActiveOrder,
  setFilters,
} from '../../../store/reducers/b2bOrders';
import {
  activeB2BOrderIdSelector,
  activeB2BOrderSelector,
  b2bOrdersErrorSelector,
  b2bOrdersFiltersSelector,
  b2bOrdersMetaSelector,
  b2bOrdersSelector,
  filteredB2BOrdersSelector,
  isB2BActiveOrderLoadingSelector,
  isB2BOrdersLoadingSelector,
} from '../../../store/selectors/b2bOrders';

export const AGENT_ORDERS_ROUTE = '/orders';
export const AGENT_ORDERS_PATH = `${AGENT_ORDERS_ROUTE}/:order_id?`;

const ORDER_ROW_HEIGHT = 116;
const INFINITE_LIST_THRESHOLD = 3;

const OrdersPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { order_id: orderIdFromUrl } = useParams<{ order_id?: string }>();
  const orders = useSelector(b2bOrdersSelector);
  const filteredOrders = useSelector(filteredB2BOrdersSelector);
  const selectedOrderId = useSelector(activeB2BOrderIdSelector);
  const selectedOrder = useSelector(activeB2BOrderSelector);
  const { query, status } = useSelector(b2bOrdersFiltersSelector);
  const meta = useSelector(b2bOrdersMetaSelector);
  const error = useSelector(b2bOrdersErrorSelector);
  const isLoading = useSelector(isB2BOrdersLoadingSelector);
  const isLoadingActiveOrder = useSelector(isB2BActiveOrderLoadingSelector);

  const debouncedQuery = useDebounce(query, 350);
  const serverQuery = [
    debouncedQuery.trim(),
    status === 'all' ? '' : `status:${status}`,
  ]
    .filter(Boolean)
    .join(' ');
  const hasNextPage = filteredOrders.length < meta.total;

  useEffect(() => {
    dispatch(
      fetchB2BOrdersAction({
        query: serverQuery,
        page: 1,
        perPage: meta.perPage,
      })
    );
  }, [dispatch, meta.perPage, serverQuery]);

  useEffect(() => {
    if (history.location.pathname === '/') {
      history.replace(AGENT_ORDERS_ROUTE);
    }
  }, [history]);

  useEffect(() => {
    if (!orderIdFromUrl) {
      if (selectedOrderId || selectedOrder || isLoadingActiveOrder) {
        dispatch(clearActiveOrder());
      }

      return;
    }

    if (selectedOrderId !== orderIdFromUrl) {
      dispatch(selectB2BOrderAction({ id: orderIdFromUrl }));
    }
  }, [
    dispatch,
    history,
    isLoadingActiveOrder,
    orderIdFromUrl,
    selectedOrder,
    selectedOrderId,
  ]);

  const handleStatusChange = (event: CustomEvent<{ value: StatusFilter }>) => {
    dispatch(setFilters({ status: event.detail.value }));
  };

  const handleSelectOrder = (id: string) => {
    if (id === selectedOrderId) return;

    history.push(`${AGENT_ORDERS_ROUTE}/${id}`);
  };

  const handleLoadMore = () => {
    if (isLoading || !hasNextPage) return;

    dispatch(
      fetchB2BOrdersAction({
        query: serverQuery,
        page: meta.page + 1,
        perPage: meta.perPage,
      })
    );
  };

  return (
    <Page>
      <Topbar>
        <div>
          <Title>Заказы</Title>
          <Muted>Бронирования и расчёты агентства</Muted>
        </div>
      </Topbar>

      <Content>
        <OrderList>
          <Filters>
            <SearchInput
              aria-label="Поиск заказов"
              placeholder="Номер, клиент, отель…"
              value={query}
              onChange={(event) =>
                dispatch(setFilters({ query: event.target.value }))
              }
            />
            <StatusSelect
              value={status}
              options={STATUS_OPTIONS}
              onChange={handleStatusChange}
            />
          </Filters>

          <ListBody>
            {error && <ListError>{error}</ListError>}
            {isLoading && !orders.length ? (
              <ListLoader />
            ) : filteredOrders.length ? (
              <InfiniteList
                key={serverQuery || 'all-orders'}
                isItemLoaded={(index) => index < filteredOrders.length}
                isNextPageLoading={isLoading}
                itemCount={
                  hasNextPage
                    ? filteredOrders.length + 1
                    : filteredOrders.length
                }
                itemData={filteredOrders}
                itemKey={(index, data) =>
                  data[index]?.id || `order-loader-${index}`
                }
                itemSize={ORDER_ROW_HEIGHT}
                minimumBatchSize={meta.perPage}
                onLoadRequest={handleLoadMore}
                threshold={INFINITE_LIST_THRESHOLD}
              >
                {({ data, index, style }) => {
                  const order = data[index];

                  if (!order) {
                    return (
                      <NextPageLoader style={style}>
                        {isLoading && <Loader size={20} />}
                      </NextPageLoader>
                    );
                  }

                  return (
                    <OrderRow
                      key={order.id}
                      style={style}
                      type="button"
                      $active={order.id === selectedOrderId}
                      onClick={() => handleSelectOrder(order.id)}
                    >
                      <RowTop>
                        <OrderNumber>№{order.id}</OrderNumber>
                        <CompactStatus status={order.status} />
                      </RowTop>
                      <HotelName title={order.hotelName}>
                        {order.hotelName}
                      </HotelName>
                      <Muted>
                        {order.touristName} · {order.dates}
                      </Muted>
                      <RowBottom>
                        <span>{order.clientPhone}</span>
                        <strong>
                          <CurrencyFormat
                            value={order.amount}
                            currencyCode="RUB"
                          />
                        </strong>
                      </RowBottom>
                    </OrderRow>
                  );
                }}
              </InfiniteList>
            ) : (
              <EmptyList>
                <strong>Заказы не найдены</strong>
                <Muted>Измените фильтр или поисковый запрос</Muted>
              </EmptyList>
            )}
          </ListBody>
        </OrderList>

        <DetailsPanel>
          {isLoadingActiveOrder ? (
            <DetailsLoader />
          ) : selectedOrder ? (
            <OrderDetails key={selectedOrder.id} order={selectedOrder} />
          ) : (
            <EmptyDetails>
              <strong>Выберите заказ</strong>
              <Muted>
                Здесь доступны туристы, состав тура, оплата и история
                бронирования.
              </Muted>
            </EmptyDetails>
          )}
        </DetailsPanel>
      </Content>
    </Page>
  );
};

export default OrdersPage;
